import { c as _c } from "react/compiler-runtime";
import { Stack } from "@mui-wrapper";
import { DesktopDateRangePicker } from "@mui/x-date-pickers-pro/DesktopDateRangePicker";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, DatePickerVariant, Text, variantMap } from "@flat-ui";
import { CustomRangeCalendarLayout } from "@components/date-range-picker/date-range-calendar-layout";
import { DateRangeSummaryField } from "@components/date-range-picker/date-range-summary-field";
import { areRangesEqualByDay, buildDefaultPresets, formatShortcutLabel, toDateRange, toDateTime, toDateTimeRange } from "@components/date-range-picker/date-range-picker-utils";

/** The width of the input field */
const INPUT_WIDTH = "12.5rem";

/** Class used to style the active shortcut chip in the calendar popover. */
const ACTIVE_SHORTCUT_CLASS = "DateRangePicker-shortcutActive";

/**
 * A customized MUI X DateRangePicker that follows the FARO design guidelines.
 * NOTE: relies on `@mui/x-date-pickers-pro`, which requires a commercial MUI X
 *
 * @returns a customized MUI DateRangePicker
 */
export function DateRangePicker(t0) {
  const $ = _c(88);
  const {
    label,
    pickerTitle: t1,
    value: sourceValue,
    defaultValue: sourceDefaultValue,
    minDate: sourceMinDate,
    maxDate: sourceMaxDate,
    presets,
    quickPresets,
    shouldEnableCompare: t2,
    compareLabel: t3,
    isCompareChecked,
    onCompareChange,
    variant: t4,
    isFullWidth: t5,
    isReadOnly: t6,
    isDisabled: t7,
    onChange,
    onAccept
  } = t0;
  const pickerTitle = t1 === undefined ? "Select date range" : t1;
  const shouldEnableCompare = t2 === undefined ? false : t2;
  const compareLabel = t3 === undefined ? "Compare to previous timeframe" : t3;
  const variant = t4 === undefined ? DatePickerVariant.Light : t4;
  const isFullWidth = t5 === undefined ? false : t5;
  const isReadOnly = t6 === undefined ? false : t6;
  const isDisabled = t7 === undefined ? false : t7;
  const [isOpen, updateIsOpen] = useState(false);
  const variantStyle = variantMap[variant];
  const inputWidth = isFullWidth ? "100%" : INPUT_WIDTH;
  const isControlled = sourceValue !== undefined;
  let t8;
  if ($[0] !== sourceDefaultValue || $[1] !== sourceValue) {
    t8 = () => toDateTimeRange(sourceValue ?? sourceDefaultValue);
    $[0] = sourceDefaultValue;
    $[1] = sourceValue;
    $[2] = t8;
  } else {
    t8 = $[2];
  }
  const [internalValue, setInternalValue] = useState(t8);
  const pickerValue = internalValue;
  let t10;
  let t9;
  if ($[3] !== isControlled || $[4] !== sourceValue) {
    t9 = () => {
      if (!isControlled) {
        return;
      }
      setInternalValue(toDateTimeRange(sourceValue));
    };
    t10 = [isControlled, sourceValue];
    $[3] = isControlled;
    $[4] = sourceValue;
    $[5] = t10;
    $[6] = t9;
  } else {
    t10 = $[5];
    t9 = $[6];
  }
  useEffect(t9, t10);
  let t11;
  if ($[7] !== sourceMinDate) {
    t11 = toDateTime(sourceMinDate);
    $[7] = sourceMinDate;
    $[8] = t11;
  } else {
    t11 = $[8];
  }
  const minDate = t11;
  let t12;
  if ($[9] !== sourceMaxDate) {
    t12 = toDateTime(sourceMaxDate);
    $[9] = sourceMaxDate;
    $[10] = t12;
  } else {
    t12 = $[10];
  }
  const maxDate = t12;
  let t13;
  if ($[11] !== onAccept || $[12] !== onChange) {
    t13 = (range, t14) => {
      const shouldAccept = t14 === undefined ? false : t14;
      setInternalValue(range);
      onChange?.(toDateRange(range));
      if (shouldAccept) {
        onAccept?.(toDateRange(range));
      }
    };
    $[11] = onAccept;
    $[12] = onChange;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  const applyRange = t13;
  let t14;
  if ($[14] !== applyRange) {
    t14 = preset => {
      const [start, end] = preset.getValue();
      applyRange([DateTime.fromJSDate(start), DateTime.fromJSDate(end)], true);
    };
    $[14] = applyRange;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  const applyPreset = t14;
  let t15;
  if ($[16] !== presets) {
    t15 = presets ?? buildDefaultPresets();
    $[16] = presets;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  let t16;
  if ($[18] !== pickerValue || $[19] !== t15) {
    let t17;
    if ($[21] !== pickerValue) {
      t17 = t18 => {
        const {
          label: label_0,
          getValue
        } = t18;
        const [start_0, end_0] = getValue();
        const presetRange = [DateTime.fromJSDate(start_0), DateTime.fromJSDate(end_0)];
        return {
          label: formatShortcutLabel(label_0),
          getValue: () => presetRange,
          className: areRangesEqualByDay(pickerValue, presetRange) ? ACTIVE_SHORTCUT_CLASS : undefined
        };
      };
      $[21] = pickerValue;
      $[22] = t17;
    } else {
      t17 = $[22];
    }
    t16 = t15.map(t17);
    $[18] = pickerValue;
    $[19] = t15;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  const shortcutsItems = t16;
  let t17;
  if ($[23] !== variantStyle) {
    t17 = () => <DateRangeSummaryField variantStyle={variantStyle} />;
    $[23] = variantStyle;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  const SummaryFieldWrapper = t17;
  let t18;
  if ($[25] !== compareLabel || $[26] !== isCompareChecked || $[27] !== onCompareChange || $[28] !== pickerTitle || $[29] !== shouldEnableCompare || $[30] !== variant || $[31] !== variantStyle) {
    t18 = props => <CustomRangeCalendarLayout variantStyle={variantStyle} isDark={variant === DatePickerVariant.Dark} pickerTitle={pickerTitle} shouldEnableCompare={shouldEnableCompare} compareLabel={compareLabel} isCompareChecked={isCompareChecked} onCompareChange={onCompareChange} {...props} />;
    $[25] = compareLabel;
    $[26] = isCompareChecked;
    $[27] = onCompareChange;
    $[28] = pickerTitle;
    $[29] = shouldEnableCompare;
    $[30] = variant;
    $[31] = variantStyle;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  const CustomLayoutWrapper = t18;
  let t19;
  if ($[33] !== label) {
    t19 = label && <Text variant="heading12">{label}</Text>;
    $[33] = label;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      alignItems: "center",
      gap: 1,
      flexWrap: "wrap"
    };
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  let t22;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = () => updateIsOpen(true);
    t22 = () => updateIsOpen(false);
    $[36] = t21;
    $[37] = t22;
  } else {
    t21 = $[36];
    t22 = $[37];
  }
  const t23 = minDate ?? undefined;
  const t24 = maxDate ?? undefined;
  const t25 = isFullWidth ? "100%" : undefined;
  let t26;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = {
      cursor: "pointer"
    };
    $[38] = t26;
  } else {
    t26 = $[38];
  }
  let t27;
  if ($[39] !== variantStyle.secondaryColor) {
    t27 = {
      color: variantStyle.secondaryColor,
      fontSize: "1.125rem"
    };
    $[39] = variantStyle.secondaryColor;
    $[40] = t27;
  } else {
    t27 = $[40];
  }
  let t28;
  if ($[41] !== inputWidth || $[42] !== t27 || $[43] !== variantStyle.backgroundColor || $[44] !== variantStyle.inputFieldHoverColor) {
    t28 = {
      height: "2.375rem",
      width: inputWidth,
      minWidth: inputWidth,
      maxWidth: inputWidth,
      backgroundColor: variantStyle.backgroundColor,
      color: variantStyle.inputFieldHoverColor,
      cursor: "pointer",
      "& .MuiInputBase-input": t26,
      "& .MuiInputAdornment-root .MuiSvgIcon-root": t27
    };
    $[41] = inputWidth;
    $[42] = t27;
    $[43] = variantStyle.backgroundColor;
    $[44] = variantStyle.inputFieldHoverColor;
    $[45] = t28;
  } else {
    t28 = $[45];
  }
  let t29;
  if ($[46] !== t25 || $[47] !== t28) {
    t29 = {
      width: t25,
      "& .MuiInputBase-root": t28
    };
    $[46] = t25;
    $[47] = t28;
    $[48] = t29;
  } else {
    t29 = $[48];
  }
  let t30;
  if ($[49] !== CustomLayoutWrapper || $[50] !== SummaryFieldWrapper) {
    t30 = {
      field: SummaryFieldWrapper,
      layout: CustomLayoutWrapper
    };
    $[49] = CustomLayoutWrapper;
    $[50] = SummaryFieldWrapper;
    $[51] = t30;
  } else {
    t30 = $[51];
  }
  let t31;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = {
      name: "flip",
      enabled: false
    };
    $[52] = t31;
  } else {
    t31 = $[52];
  }
  let t32;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = {
      placement: "bottom-start",
      modifiers: [t31, {
        name: "preventOverflow",
        options: {
          altAxis: true,
          tether: false,
          padding: 8
        }
      }]
    };
    $[53] = t32;
  } else {
    t32 = $[53];
  }
  let t33;
  if ($[54] !== shortcutsItems) {
    t33 = {
      items: shortcutsItems,
      changeImportance: "set"
    };
    $[54] = shortcutsItems;
    $[55] = t33;
  } else {
    t33 = $[55];
  }
  let t34;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = {
      actions: ["cancel", "accept"]
    };
    $[56] = t34;
  } else {
    t34 = $[56];
  }
  let t35;
  if ($[57] !== t33) {
    t35 = {
      popper: t32,
      shortcuts: t33,
      actionBar: t34
    };
    $[57] = t33;
    $[58] = t35;
  } else {
    t35 = $[58];
  }
  let t36;
  let t37;
  if ($[59] !== applyRange) {
    t36 = range_0 => applyRange(range_0);
    t37 = range_1 => {
      updateIsOpen(false);
      const completeRange = [range_1[0], range_1[1] ?? range_1[0]];
      applyRange(completeRange, true);
    };
    $[59] = applyRange;
    $[60] = t36;
    $[61] = t37;
  } else {
    t36 = $[60];
    t37 = $[61];
  }
  let t38;
  if ($[62] !== isDisabled || $[63] !== isOpen || $[64] !== isReadOnly || $[65] !== pickerValue || $[66] !== t23 || $[67] !== t24 || $[68] !== t29 || $[69] !== t30 || $[70] !== t35 || $[71] !== t36 || $[72] !== t37) {
    t38 = <DesktopDateRangePicker open={isOpen} disabled={isDisabled} readOnly={isReadOnly} onOpen={t21} onClose={t22} closeOnSelect={false} value={pickerValue} minDate={t23} maxDate={t24} calendars={1} showDaysOutsideCurrentMonth={true} reduceAnimations={true} disableAutoMonthSwitching={true} sx={t29} slots={t30} slotProps={t35} onChange={t36} onAccept={t37} />;
    $[62] = isDisabled;
    $[63] = isOpen;
    $[64] = isReadOnly;
    $[65] = pickerValue;
    $[66] = t23;
    $[67] = t24;
    $[68] = t29;
    $[69] = t30;
    $[70] = t35;
    $[71] = t36;
    $[72] = t37;
    $[73] = t38;
  } else {
    t38 = $[73];
  }
  let t39;
  if ($[74] !== applyPreset || $[75] !== isDisabled || $[76] !== isReadOnly || $[77] !== quickPresets || $[78] !== variantStyle.inputFieldDefaultBorderColor || $[79] !== variantStyle.secondaryColor || $[80] !== variantStyle.selectedColor) {
    t39 = quickPresets?.map(preset_0 => <Button key={preset_0.label} variant="secondary" disabled={isDisabled || isReadOnly} onClick={() => applyPreset(preset_0)} sx={{
      height: "2.375rem",
      whiteSpace: "nowrap",
      color: variantStyle.secondaryColor,
      borderColor: variantStyle.inputFieldDefaultBorderColor,
      "&:hover": {
        borderColor: variantStyle.selectedColor,
        color: variantStyle.selectedColor,
        backgroundColor: "transparent"
      }
    }}>{preset_0.label}</Button>);
    $[74] = applyPreset;
    $[75] = isDisabled;
    $[76] = isReadOnly;
    $[77] = quickPresets;
    $[78] = variantStyle.inputFieldDefaultBorderColor;
    $[79] = variantStyle.secondaryColor;
    $[80] = variantStyle.selectedColor;
    $[81] = t39;
  } else {
    t39 = $[81];
  }
  let t40;
  if ($[82] !== t38 || $[83] !== t39) {
    t40 = <Stack direction="row" sx={t20}>{t38}{t39}</Stack>;
    $[82] = t38;
    $[83] = t39;
    $[84] = t40;
  } else {
    t40 = $[84];
  }
  let t41;
  if ($[85] !== t19 || $[86] !== t40) {
    t41 = <>{t19}{t40}</>;
    $[85] = t19;
    $[86] = t40;
    $[87] = t41;
  } else {
    t41 = $[87];
  }
  return t41;
}